import { useRef, useCallback } from 'react';
import { toast, Id } from 'react-toastify';
import soundService from '../services/soundService';

interface QueuedOrder {
  order_number: string;
  total_amount: number | string;
}

/**
 * Hook สำหรับ batch notification ของ Order ใหม่
 * - รวม order ที่เข้ามาภายใน BATCH_DELAY ms เป็น toast เดียว
 * - เล่นเสียงแค่ครั้งเดียวต่อ batch
 * - อัปเดต toast เดิมแทนที่จะสร้างใหม่ซ้ำ
 */
export function useOrderNotification() {
  const queueRef = useRef<QueuedOrder[]>([]);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const toastIdRef = useRef<Id | null>(null);

  const BATCH_DELAY = 2000; // รอรวม 2 วินาที

  const flush = useCallback(() => {
    const orders = queueRef.current;
    if (orders.length === 0) return;

    // สร้าง toast content
    let content: string;
    if (orders.length === 1) {
      const o = orders[0];
      const amt = typeof o.total_amount === 'string' ? parseFloat(o.total_amount) : o.total_amount;
      content = `Order ใหม่: ${o.order_number} • ฿${amt.toLocaleString('th-TH', { minimumFractionDigits: 2 })}`;
    } else {
      const totalAmt = orders.reduce((sum, o) => {
        const amt = typeof o.total_amount === 'string' ? parseFloat(o.total_amount) : o.total_amount;
        return sum + amt;
      }, 0);
      content = `Order ใหม่ ${orders.length} รายการ • รวม ฿${totalAmt.toLocaleString('th-TH', { minimumFractionDigits: 2 })}`;
    }

    // อัปเดต toast เดิม หรือสร้างใหม่
    // if (toastIdRef.current && toast.isActive(toastIdRef.current)) {
    //   toast.update(toastIdRef.current, {
    //     render: content,
    //     autoClose: 4000,
    //   });
    // } else {
    //   toastIdRef.current = toast.info(content, {
    //     autoClose: 4000,
    //     onClose: () => { toastIdRef.current = null; },
    //   });
    // }

    queueRef.current = [];
  }, []);

  const notifyNewOrder = useCallback((order: { order_number: string; total_amount: number | string }) => {
    // เล่นเสียงแค่ตัวแรกของ batch
    if (queueRef.current.length === 0) {
      soundService.playDepositSound();
    }

    queueRef.current.push(order);

    // รีเซ็ต timer — รอรวมเพิ่ม
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flush, BATCH_DELAY);

    // ถ้า queue เริ่มเยอะมาก (>10) flush ทันที
    if (queueRef.current.length >= 10) {
      if (timerRef.current) clearTimeout(timerRef.current);
      flush();
    }
  }, [flush]);

  const notifyPaid = useCallback((order: { order_number: string; amount: number | string }) => {
    soundService.playDepositSound();
    const amt = typeof order.amount === 'string' ? parseFloat(order.amount) : order.amount;
    // toast.success(
    //   `ชำระเงินสำเร็จ: ${order.order_number} • ฿${amt.toLocaleString('th-TH', { minimumFractionDigits: 2 })}`,
    //   { autoClose: 5000 }
    // );
  }, []);

  return { notifyNewOrder, notifyPaid };
}
